(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const find = (root, selector) => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
  const initialize = (root = document) => {
    find(root, '[data-fw27-hero-media]').forEach((stage) => {
      if (stage.dataset.mediaReady) return;
      stage.dataset.mediaReady = 'true';
      const garment = stage.querySelector('[data-fw27-object]');
      const image = garment?.querySelector('img');
      const panel = stage.querySelector('[data-fw27-model]');
      const video = panel?.querySelector('video');
      const toggle = stage.querySelector('[data-fw27-motion]');
      const controls = stage.querySelector('[data-fw27-hero-controls]');
      const buttons = [...stage.querySelectorAll('[data-fw27-view]')];
      const status = stage.querySelector('[data-fw27-hero-status]');
      const storyToggle = stage.querySelector('[data-fw27-story-toggle]');
      const sceneNumber = stage.querySelector('[data-fw27-scene-number]');
      const sceneLabel = stage.querySelector('[data-fw27-scene-label]');
      const saveData = Boolean(navigator.connection?.saveData);
      if (!image || !video || !toggle || !controls) return;
      controls.hidden = false;
      let frame = 0;
      let current = 'product';
      let viewTimer;
      let storyTimer;
      let storyPaused = reduced.matches || saveData;
      stage.dataset.view = current;
      const syncStoryToggle = () => {
        if (!storyToggle) return;
        storyToggle.hidden = reduced.matches || saveData;
        storyToggle.textContent = storyPaused ? 'Play story' : 'Pause story';
        storyToggle.setAttribute('aria-pressed', String(!storyPaused));
      };
      const stopStoryClock = () => {
        clearTimeout(storyTimer);
        stage.classList.remove('is-story-running');
      };
      const scheduleStory = () => {
        stopStoryClock();
        if (storyPaused || document.hidden || reduced.matches || saveData || buttons.length < 2) return;
        requestAnimationFrame(() => stage.classList.add('is-story-running'));
        storyTimer = setTimeout(() => {
          const index = Math.max(0, buttons.findIndex((button) => button.dataset.fw27View === current));
          activate(buttons[(index + 1) % buttons.length], false);
        }, 6500);
      };
      const resetTilt = () => {
        cancelAnimationFrame(frame);
        garment.style.setProperty('--fw-x', '0deg');
        garment.style.setProperty('--fw-y', '0deg');
      };
      stage.addEventListener('pointermove', (event) => {
        if (reduced.matches || !finePointer.matches || garment.hidden) return;
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const rect = stage.getBoundingClientRect();
          garment.style.setProperty('--fw-x', `${(0.5 - (event.clientY - rect.top) / rect.height) * 5}deg`);
          garment.style.setProperty('--fw-y', `${((event.clientX - rect.left) / rect.width - 0.5) * 8}deg`);
        });
      });
      stage.addEventListener('pointerleave', resetTilt);
      const sync = () => {
        const label = video.paused ? stage.dataset.playLabel : stage.dataset.pauseLabel;
        toggle.textContent = label;
        toggle.setAttribute('aria-label', label);
      };
      const play = () => video.play().catch(() => {
        sync();
        if (status) status.textContent = stage.dataset.errorLabel;
      });
      const activate = (button, userInitiated = true) => {
        current = button.dataset.fw27View;
        stage.dataset.view = current;
        clearTimeout(viewTimer);
        stage.classList.remove('is-changing');
        if (!reduced.matches) {
          requestAnimationFrame(() => stage.classList.add('is-changing'));
          viewTimer = setTimeout(() => stage.classList.remove('is-changing'), 450);
        }
        video.pause();
        resetTilt();
        const productView = current === 'product' || current === 'back';
        buttons.forEach((item) => item.setAttribute('aria-pressed', String(item === button)));
        const sceneIndex = buttons.indexOf(button);
        if (sceneNumber) sceneNumber.textContent = String(sceneIndex + 1).padStart(2, '0');
        if (sceneLabel) sceneLabel.textContent = button.dataset.scene || button.textContent.trim();
        garment.hidden = !productView;
        panel.hidden = productView;
        toggle.hidden = productView || !button.dataset.film;
        if (productView) {
          image.removeAttribute('srcset');
          image.src = button.dataset.image;
          image.alt = button.dataset.alt;
        } else {
          video.poster = button.dataset.poster;
          video.setAttribute('aria-label', `${image.alt.split(' — ')[0]} — ${button.textContent.trim()}`);
          if (button.dataset.film && video.getAttribute('src') !== button.dataset.film) {
            video.src = button.dataset.film;
            video.load();
          } else if (!button.dataset.film) {
            video.removeAttribute('src');
            video.load();
          }
          if (button.dataset.film && !reduced.matches && (userInitiated || !storyPaused)) play();
        }
        if (status) status.textContent = button.textContent.trim();
        sync();
        scheduleStory();
      };
      buttons.forEach((button) => button.addEventListener('click', () => {
        if (current !== button.dataset.fw27View) activate(button);
      }));
      toggle.addEventListener('click', () => video.paused ? play() : video.pause());
      storyToggle?.addEventListener('click', () => {
        storyPaused = !storyPaused;
        if (storyPaused) {
          stopStoryClock();
          video.pause();
        } else {
          if (garment.hidden && video.src) play();
          scheduleStory();
        }
        syncStoryToggle();
      });
      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      video.addEventListener('error', () => {
        sync();
        if (status) status.textContent = stage.dataset.errorLabel;
      });
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          video.pause();
          stopStoryClock();
        } else {
          scheduleStory();
        }
      });
      reduced.addEventListener('change', () => {
        storyPaused = reduced.matches || saveData;
        if (reduced.matches) { video.pause(); resetTilt(); stopStoryClock(); }
        syncStoryToggle();
        scheduleStory();
      });
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          if (!entries[0].isIntersecting) {
            video.pause();
            stopStoryClock();
          } else {
            scheduleStory();
          }
        }, { threshold: 0.1 });
        observer.observe(stage);
        document.addEventListener('shopify:section:unload', (event) => {
          if (event.target.contains(stage)) { observer.disconnect(); video.pause(); cancelAnimationFrame(frame); stopStoryClock(); }
        });
      }
      const initial = buttons.find((button) => button.dataset.fw27View === stage.dataset.defaultView);
      activate(initial || buttons[0], false);
      syncStoryToggle();
    });
    if (!reduced.matches && 'IntersectionObserver' in window) {
      document.documentElement.classList.add('fw27-reveal-ready');
      const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.remove('fw27-reveal-pending'); observer.unobserve(entry.target); }
      }), { threshold: 0.08 });
      find(root, '[data-fw27-reveal]').forEach((element) => {
        if (element.getBoundingClientRect().top > window.innerHeight) {
          element.classList.add('fw27-reveal-pending');
          observer.observe(element);
        }
      });
    }
  };
  initialize();
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
